import { useFormContext, Controller } from 'react-hook-form';
import {
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@client/new/components/basic';
import { FieldTitle } from './RedStarTitle';
import { FieldDesc } from './common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

type CTAInputProps = {
  index: number;
  ctaIndex: 1 | 2;
  label?: string;
  defaultText?: string;
  defaultLink?: string;
  onReset?: () => void;
};

export function CTAInput({
  index,
  ctaIndex,
  label,
  defaultText,
  defaultLink,
  onReset,
}: CTAInputProps) {
  const { register, watch, control } = useFormContext();
  const basePath = `sections.${index}.cta${ctaIndex}`;
  const r = (field: string) => register(`${basePath}.${field}`);

  const enabled = watch(`${basePath}.enabled`);

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3">
        <FieldTitle title={label || `CTA ${ctaIndex}`} optional />
        <Tooltip title="Leave the button text and link empty to use the default values.">
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-gray-500 cursor-help text-sm"
          />
        </Tooltip>
        <Controller
          name={`${basePath}.enabled`}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch {...field} checked={field.value || false} />}
              label=""
            />
          )}
        />
        {onReset && (
          <Tooltip title="Reset to default values">
            <IconButton onClick={onReset} size="small">
              <FontAwesomeIcon icon={faRotateLeft} className="text-sm" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <FieldDesc>
        Configure the call-to-action button for this section
      </FieldDesc>

      {enabled && (
        <>
          <div className="mt-4">
            <FieldTitle title="Button Text" optional />
            <TextField
              {...r('text')}
              className="mb-[5px] max-w-[680px] w-full"
              inputProps={{
                placeholder: defaultText || 'e.g., Get Started, Learn More',
                maxLength: 50,
              }}
            />
          </div>

          <div className="mt-4">
            <FieldTitle title="Button Link" optional />
            <TextField
              {...r('link')}
              className="mb-[5px] max-w-[680px] w-full"
              inputProps={{
                placeholder: defaultLink || 'https://example.com',
                maxLength: 500,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
